import React, { useState } from 'react';
import {
  Brain,
  Layers,
  GitBranch,
  TestTube,
  BarChart,
  Plus,
  Trash2,
  Copy,
  Save,
  Play,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Lightbulb,
  BookOpen,
  Code,
  FileText,
  Zap,
  Bot,
  AlertCircle
} from 'lucide-react';
import { 
  ChainOfThoughtTemplate, 
  FewShotExample, 
  ABTestConfig,
  PromptVersion 
} from '../types/prompt';
import { chainOfThoughtTemplates } from '../data/multiModelTemplates';
import useLocalStorage from '../hooks/useLocalStorage';
import { useApiKeys } from '../contexts/ApiKeyContext';
import aiService from '../services/aiService';
import { useAuth } from '../contexts/AuthContext';
import { promptsService, historyService } from '../services/firebase/firestore';
import toast from 'react-hot-toast';

const AdvancedPromptEngineering: React.FC = () => {
  const { getApiKeyForModel, isKeyConfigured } = useApiKeys();
  const { user, isAuthenticated, trackUsage } = useAuth();
  const [activeTab, setActiveTab] = useState<'chain' | 'fewshot' | 'version' | 'abtest'>('chain');
  const [selectedCoT, setSelectedCoT] = useState<ChainOfThoughtTemplate | null>(null);
  const [cotSteps, setCotSteps] = useState<Record<string, string>>({});
  const [generatedCoTPrompt, setGeneratedCoTPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Get API keys for AI features
  const openAIKey = getApiKeyForModel('gpt4');
  const claudeKey = getApiKeyForModel('claude');
  const geminiKey = getApiKeyForModel('gemini');
  const hasAnyKey = !!(openAIKey || claudeKey || geminiKey);
  
  // Few-shot learning state
  const [fewShotExamples, setFewShotExamples] = useLocalStorage<FewShotExample[]>('fewShotExamples', []);
  const [newExample, setNewExample] = useState<FewShotExample>({
    id: '',
    input: '',
    output: '',
    explanation: '',
    category: ''
  });
  const [fewShotPrompt, setFewShotPrompt] = useState('');
  
  // Version control state
  const [promptVersions, setPromptVersions] = useLocalStorage<PromptVersion[]>('promptVersions', []);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [versionNote, setVersionNote] = useState('');
  
  // A/B testing state
  const [abTests, setAbTests] = useLocalStorage<ABTestConfig[]>('abTests', []);
  const [currentTest, setCurrentTest] = useState<ABTestConfig | null>(null);
  const [newVariant, setNewVariant] = useState({ name: '', prompt: '' });

  // Chain of Thought Functions
  const selectCoTTemplate = (template: ChainOfThoughtTemplate) => {
    setSelectedCoT(template);
    const initialSteps: Record<string, string> = {};
    template.steps.forEach(step => {
      initialSteps[step.id] = '';
    });
    setCotSteps(initialSteps);
  };

  const generateCoTPrompt = () => {
    if (!selectedCoT) return;
    
    let prompt = '';
    selectedCoT.steps.forEach(step => {
      prompt += `${step.instruction}\n`;
      if (cotSteps[step.id]) {
        prompt += `${cotSteps[step.id]}\n\n`;
      }
      if (step.validation) {
        prompt += `(Validation: ${step.validation})\n\n`;
      }
    });
    
    const finalPrompt = selectedCoT.finalPromptTemplate
      .replace('{all_steps}', prompt)
      .replace('{solution}', '[Your solution here]')
      .replace('{conclusion}', '[Your conclusion here]')
      .replace('{confidence_level}', '[High/Medium/Low]')
      .replace('{output}', '[Your output here]');
    
    setGeneratedCoTPrompt(finalPrompt);
  };

  // AI-Enhanced Chain of Thought
  const enhanceCoTWithAI = async () => {
    if (!selectedCoT || !hasAnyKey) return;
    
    setLoading(true);
    try {
      const model = openAIKey ? 'gpt4' : claudeKey ? 'claude' : 'gemini';
      const apiKey = openAIKey || claudeKey || geminiKey;
      
      // First generate the basic prompt
      generateCoTPrompt();
      
      // Then enhance it with AI
      if (generatedCoTPrompt) {
        const response = await aiService.enhancePrompt(generatedCoTPrompt, model, apiKey!);
        if (response.success && response.data) {
          setGeneratedCoTPrompt(response.data.enhanced);
        }
      }
    } catch (error) {
      console.error('Error enhancing prompt:', error);
    } finally {
      setLoading(false);
    }
  };

  // Few-shot Learning Functions
  const addFewShotExample = () => {
    if (!newExample.input || !newExample.output) return;
    
    const example: FewShotExample = {
      ...newExample,
      id: Date.now().toString(),
      quality: 100
    };
    
    setFewShotExamples([...fewShotExamples, example]);
    setNewExample({ id: '', input: '', output: '', explanation: '', category: '' });
  };

  const removeFewShotExample = (id: string) => {
    setFewShotExamples(fewShotExamples.filter(ex => ex.id !== id));
  };

  const generateFewShotPrompt = () => {
    if (fewShotExamples.length === 0) return;
    
    let prompt = 'Here are some examples:\n\n';
    
    fewShotExamples.forEach((example, index) => {
      prompt += `Example ${index + 1}:\n`;
      prompt += `Input: ${example.input}\n`;
      prompt += `Output: ${example.output}\n`;
      if (example.explanation) {
        prompt += `Explanation: ${example.explanation}\n`;
      }
      prompt += '\n';
    });
    
    prompt += `Now, based on these examples, ${fewShotPrompt || '[your task here]'}`;
    
    return prompt;
  };

  // Version Control Functions
  const saveVersion = () => {
    if (!currentPrompt) return;
    
    const version: PromptVersion = {
      id: Date.now().toString(),
      promptId: 'main',
      version: promptVersions.length + 1,
      content: currentPrompt,
      changes: versionNote || 'No description provided',
      timestamp: new Date()
    };
    
    setPromptVersions([...promptVersions, version]);
    setVersionNote('');
  };

  const loadVersion = (version: PromptVersion) => {
    setCurrentPrompt(version.content);
  };

  // A/B Testing Functions
  const createABTest = () => {
    const test: ABTestConfig = {
      id: Date.now().toString(),
      name: `Test ${abTests.length + 1}`,
      variants: [],
      status: 'draft'
    };
    
    setCurrentTest(test);
    setAbTests([...abTests, test]);
  };

  const addVariant = () => {
    if (!currentTest || !newVariant.name || !newVariant.prompt) return;
    
    const updatedTest = {
      ...currentTest,
      variants: [...currentTest.variants, {
        id: Date.now().toString(),
        name: newVariant.name,
        prompt: newVariant.prompt,
        metrics: {}
      }]
    };
    
    setCurrentTest(updatedTest);
    setAbTests(abTests.map(t => t.id === currentTest.id ? updatedTest : t));
    setNewVariant({ name: '', prompt: '' });
  };

  const runABTest = () => {
    if (!currentTest || currentTest.variants.length < 2) return;
    
    // Simulate running the test
    const updatedTest = {
      ...currentTest,
      status: 'running' as const,
      startDate: new Date()
    };
    
    // Simulate metrics (in real app, these would come from actual usage)
    updatedTest.variants = updatedTest.variants.map(v => ({
      ...v,
      metrics: {
        impressions: Math.floor(Math.random() * 1000),
        conversions: Math.floor(Math.random() * 100),
        avgScore: Math.random() * 5
      }
    }));
    
    setCurrentTest(updatedTest);
    setAbTests(abTests.map(t => t.id === currentTest.id ? updatedTest : t));
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-xl p-6 mb-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <Brain className="w-10 h-10" />
          <h1 className="text-3xl font-bold">Advanced Prompt Engineering</h1>
        </div>
        <p className="text-lg opacity-90">
          Professional tools for creating, testing, and optimizing AI prompts
        </p>
      </div>

      {/* API Status */}
      {!hasAnyKey && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">AI Features Available</p>
            <p className="text-xs text-gray-600 mt-1">
              Configure API keys in settings (key icon in header) to enable AI-powered prompt enhancement, optimization, and testing features.
            </p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-md p-2 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('chain')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'chain'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Layers className="w-4 h-4" />
            Chain of Thought
          </button>
          <button
            onClick={() => setActiveTab('fewshot')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'fewshot'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Few-Shot Learning
          </button>
          <button
            onClick={() => setActiveTab('version')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'version'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            Version Control
          </button>
          <button
            onClick={() => setActiveTab('abtest')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'abtest'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <TestTube className="w-4 h-4" />
            A/B Testing
          </button>
        </div>
      </div>

      {/* Chain of Thought Tab */}
      {activeTab === 'chain' && (
        <div className="space-y-6">
          {/* Template Selection */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Select Chain of Thought Template
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {chainOfThoughtTemplates.map(template => (
                <div
                  key={template.id}
                  onClick={() => selectCoTTemplate(template)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedCoT?.id === template.id
                      ? 'border-purple-500 bg-purple-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900 mb-2">{template.name}</h4>
                  <p className="text-sm text-gray-600">{template.description}</p>
                  <div className="mt-3">
                    <span className="text-xs text-purple-600 font-medium">
                      {template.steps.length} steps
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Step-by-step Builder */}
            {selectedCoT && (
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Build Your Chain of Thought</h4>
                
                {selectedCoT.steps.map((step, index) => (
                  <div key={step.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{step.instruction}</p>
                        {step.example && (
                          <p className="text-sm text-gray-500 mt-1">
                            <span className="font-medium">Example:</span> {step.example}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <textarea
                      value={cotSteps[step.id] || ''}
                      onChange={(e) => setCotSteps({ ...cotSteps, [step.id]: e.target.value })}
                      placeholder={`Enter content for: ${step.instruction}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      rows={3}
                    />
                    
                    {step.validation && (
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {step.validation}
                      </p>
                    )}
                  </div>
                ))}
                
                <div className="flex gap-3">
                  <button
                    onClick={generateCoTPrompt}
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  >
                    <Brain className="w-5 h-5" />
                    Generate Chain of Thought
                  </button>
                  {hasAnyKey && (
                    <button
                      onClick={enhanceCoTWithAI}
                      disabled={loading}
                      className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Bot className="w-5 h-5" />
                      {loading ? 'Enhancing...' : 'Enhance with AI'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Generated Prompt */}
            {generatedCoTPrompt && (
              <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">Generated CoT Prompt</h4>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedCoTPrompt);
                      toast.success('📋 Copied to clipboard!');
                    }}
                    className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                  <button
                    onClick={async () => {
                      if (!isAuthenticated || !user) {
                        toast.error('Please sign in to save prompts');
                        return;
                      }
                      
                      const savePromise = (async () => {
                        const promptData = {
                          userId: user.uid,
                          title: `CoT: ${selectedCoT?.name || 'Chain of Thought'} - ${new Date().toLocaleDateString()}`,
                          content: generatedCoTPrompt,
                          platform: 'universal' as const,
                          category: 'text' as const,
                          tags: ['chain-of-thought', 'advanced', selectedCoT?.category || 'general'],
                          parameters: cotSteps,
                          isPublic: false,
                          isFavorite: false
                        };
                        
                        const promptId = await promptsService.create(promptData);
                        await trackUsage('promptsSaved');
                        await historyService.add({
                          userId: user.uid,
                          action: 'saved',
                          content: generatedCoTPrompt,
                          platform: 'universal'
                        });
                        
                        return promptId;
                      })();
                      
                      toast.promise(
                        savePromise,
                        {
                          loading: 'Saving CoT prompt...',
                          success: '✅ Chain of Thought prompt saved!',
                          error: (err) => `Failed to save: ${err.message || err}`
                        }
                      );
                    }}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" />
                    Save
                  </button>
                </div>
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-white p-3 rounded">
                  {generatedCoTPrompt}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Few-Shot Learning Tab */}
      {activeTab === 'fewshot' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" />
              Few-Shot Learning Builder
            </h3>

            {/* Add Example Form */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Add Training Example</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Input
                  </label>
                  <textarea
                    value={newExample.input}
                    onChange={(e) => setNewExample({ ...newExample, input: e.target.value })}
                    placeholder="Example input..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expected Output
                  </label>
                  <textarea
                    value={newExample.output}
                    onChange={(e) => setNewExample({ ...newExample, output: e.target.value })}
                    placeholder="Expected output..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category (optional)
                  </label>
                  <input
                    type="text"
                    value={newExample.category}
                    onChange={(e) => setNewExample({ ...newExample, category: e.target.value })}
                    placeholder="e.g., Classification, Generation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Explanation (optional)
                  </label>
                  <input
                    type="text"
                    value={newExample.explanation}
                    onChange={(e) => setNewExample({ ...newExample, explanation: e.target.value })}
                    placeholder="Why this output?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <button
                onClick={addFewShotExample}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Example
              </button>
            </div>

            {/* Examples List */}
            {fewShotExamples.length > 0 && (
              <div className="space-y-3 mb-6">
                <h4 className="font-medium text-gray-900">Training Examples ({fewShotExamples.length})</h4>
                
                {fewShotExamples.map((example, index) => (
                  <div key={example.id} className="border rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-sm text-gray-700">
                        Example {index + 1}
                        {example.category && (
                          <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {example.category}
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => removeFewShotExample(example.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="font-medium text-gray-600">Input:</p>
                        <p className="text-gray-700 bg-gray-50 p-2 rounded mt-1">
                          {example.input}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Output:</p>
                        <p className="text-gray-700 bg-gray-50 p-2 rounded mt-1">
                          {example.output}
                        </p>
                      </div>
                    </div>
                    
                    {example.explanation && (
                      <p className="text-xs text-gray-500 mt-2">
                        💡 {example.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Task Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Description
              </label>
              <input
                type="text"
                value={fewShotPrompt}
                onChange={(e) => setFewShotPrompt(e.target.value)}
                placeholder="Describe what the model should do with these examples..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Generate Button */}
            {fewShotExamples.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">Generated Few-Shot Prompt</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generateFewShotPrompt() || '');
                        toast.success('📋 Copied to clipboard!');
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                    <button
                      onClick={async () => {
                        if (!isAuthenticated || !user) {
                          toast.error('Please sign in to save prompts');
                          return;
                        }
                        
                        const generatedPrompt = generateFewShotPrompt();
                        if (!generatedPrompt) {
                          toast.error('No prompt to save');
                          return;
                        }
                        
                        const savePromise = (async () => {
                          const promptData = {
                            userId: user.uid,
                            title: `Few-Shot: ${selectedFewShot?.name || 'Learning'} - ${new Date().toLocaleDateString()}`,
                            content: generatedPrompt,
                            platform: 'universal' as const,
                            category: 'text' as const,
                            tags: ['few-shot', 'advanced', selectedFewShot?.category || 'general'],
                            parameters: {
                              examples: fewShotExamples,
                              taskDescription: fewShotPrompt
                            },
                            isPublic: false,
                            isFavorite: false
                          };
                          
                          const promptId = await promptsService.create(promptData);
                          await trackUsage('promptsSaved');
                          await historyService.add({
                            userId: user.uid,
                            action: 'saved',
                            content: generatedPrompt,
                            platform: 'universal'
                          });
                          
                          return promptId;
                        })();
                        
                        toast.promise(
                          savePromise,
                          {
                            loading: 'Saving Few-Shot prompt...',
                            success: '✅ Few-Shot prompt saved!',
                            error: (err) => `Failed to save: ${err.message || err}`
                          }
                        );
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
                    >
                      <Save className="w-3 h-3" />
                      Save
                    </button>
                  </div>
                </div>
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-white p-3 rounded">
                  {generateFewShotPrompt()}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Version Control Tab */}
      {activeTab === 'version' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-green-500" />
              Prompt Version Control
            </h3>

            {/* Current Prompt Editor */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Prompt
              </label>
              <textarea
                value={currentPrompt}
                onChange={(e) => setCurrentPrompt(e.target.value)}
                placeholder="Enter your prompt here..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                rows={6}
              />
            </div>

            {/* Save Version */}
            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={versionNote}
                onChange={(e) => setVersionNote(e.target.value)}
                placeholder="Version description (e.g., 'Added more context')"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={saveVersion}
                disabled={!currentPrompt}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Version
              </button>
            </div>

            {/* Version History */}
            {promptVersions.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Version History</h4>
                
                {promptVersions.slice().reverse().map(version => (
                  <div key={version.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-medium text-sm text-gray-900">
                          Version {version.version}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {new Date(version.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <button
                        onClick={() => loadVersion(version)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
                      >
                        Load
                      </button>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{version.changes}</p>
                    
                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-700 font-mono truncate">
                        {version.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* A/B Testing Tab */}
      {activeTab === 'abtest' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <TestTube className="w-5 h-5 text-purple-500" />
                A/B Testing Framework
              </h3>
              <button
                onClick={createABTest}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Test
              </button>
            </div>

            {/* Current Test */}
            {currentTest && (
              <div className="border rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">{currentTest.name}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    currentTest.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                    currentTest.status === 'running' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {currentTest.status}
                  </span>
                </div>

                {/* Add Variant */}
                {currentTest.status === 'draft' && (
                  <div className="bg-purple-50 rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-gray-900 mb-3">Add Variant</h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newVariant.name}
                        onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                        placeholder="Variant name"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        value={newVariant.prompt}
                        onChange={(e) => setNewVariant({ ...newVariant, prompt: e.target.value })}
                        placeholder="Prompt content"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <button
                      onClick={addVariant}
                      className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Variant
                    </button>
                  </div>
                )}

                {/* Variants */}
                {currentTest.variants.length > 0 && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-gray-900">Variants</h5>
                    
                    {currentTest.variants.map((variant, index) => (
                      <div key={variant.id} className="border rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-sm">
                            {variant.name}
                            {currentTest.winner === variant.id && (
                              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                                Winner
                              </span>
                            )}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{variant.prompt}</p>
                        
                        {variant.metrics && Object.keys(variant.metrics).length > 0 && (
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs text-gray-500">Impressions</p>
                              <p className="text-sm font-semibold">{variant.metrics.impressions}</p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs text-gray-500">Conversions</p>
                              <p className="text-sm font-semibold">{variant.metrics.conversions}</p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs text-gray-500">Avg Score</p>
                              <p className="text-sm font-semibold">
                                {variant.metrics.avgScore?.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Run Test Button */}
                {currentTest.status === 'draft' && currentTest.variants.length >= 2 && (
                  <button
                    onClick={runABTest}
                    className="mt-4 w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    Start A/B Test
                  </button>
                )}
              </div>
            )}

            {/* Test History */}
            {abTests.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">All Tests</h4>
                
                {abTests.map(test => (
                  <div
                    key={test.id}
                    onClick={() => setCurrentTest(test)}
                    className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{test.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        test.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                        test.status === 'running' ? 'bg-blue-100 text-blue-600' :
                        'bg-green-100 text-green-600'
                      }`}>
                        {test.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {test.variants.length} variants
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200 mt-6">
        <h3 className="text-lg font-semibold mb-3 text-indigo-900 flex items-center gap-2">
          <Lightbulb className="w-5 h-5" />
          Advanced Prompting Tips
        </h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span><strong>Chain of Thought:</strong> Break complex tasks into logical steps for better reasoning</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span><strong>Few-Shot Learning:</strong> Provide 3-5 high-quality examples for consistent outputs</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span><strong>Version Control:</strong> Track prompt iterations to identify improvements</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span><strong>A/B Testing:</strong> Compare variants with real metrics to optimize performance</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdvancedPromptEngineering;